<script setup lang="ts">
import type { InstallationValue, ReturnValue } from '@/utils/PortalConstants';
import type {
  FinisherModelLookupItem,
  ProductModelLookupItem,
} from '@/helpers/fetchers';
import type { BookingProduct } from '@/models/advancedJobBooking';

import { computed, ref, watch } from 'vue';

import { PREDEFINED_WORKSHOP_TASK_OPTIONS } from '@/utils/PortalConstants';
import JobBookingServiceOrReturnField from '@/components/fields/JobBookingServiceOrReturnField.vue';
import KMultipleSelectField from '@/components/fields/KMultipleSelectField.vue';
import KNumberStepperField from '@/components/fields/KNumberStepperField.vue';
import KSwitchField from '@/components/fields/KSwitchField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import FinisherModelLookup from '@/components/lookups/FinisherModelLookup.vue';
import ProductModelLookup from '@/components/lookups/ProductModelLookup.vue';
import { fetchGetProductLocation } from '@/helpers/fetchers';
import { requiredValidationRule } from '@/helpers/validationRules';
import { createEmptyBookingProduct } from '@/models/advancedJobBooking';

export interface Props {
  isReturnLeg?: boolean;
  modelWarning?: string;
  isDeletable?: boolean;
  customerCode?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isReturnLeg: false,
  modelWarning: '',
  isDeletable: false,
  customerCode: '',
});

const emit = defineEmits<{
  deleteBtnClick: [event: Event];
}>();

const model = defineModel<BookingProduct>({
  default: () => createEmptyBookingProduct(),
});

// Clear the inactive value when the leg type changes so submit never sends
// both `installation` and `returns` populated at once.
watch(
  () => props.isReturnLeg,
  (isReturnLeg) => {
    model.value = isReturnLeg
      ? { ...model.value, installation: '' }
      : { ...model.value, returns: '' };
  },
);

function handleDeleteClick(event: Event): void {
  emit('deleteBtnClick', event);
}

const workshopRequired = computed({
  get: () => model.value.predefined_workshop || model.value.custom_workshop,
  set: (value: boolean) => {
    if (!value) {
      model.value = {
        ...model.value,
        predefined_workshop: false,
        custom_workshop: false,
      };
    } else if (
      !model.value.predefined_workshop &&
      !model.value.custom_workshop
    ) {
      model.value = { ...model.value, predefined_workshop: true };
    }
  },
});

const predefinedTasksValue = computed({
  get: () => model.value.predefined_workshop_tasks ?? [],
  set: (value: string[]) => {
    model.value = { ...model.value, predefined_workshop_tasks: value };
  },
});

const customTaskValue = computed({
  get: () => model.value.custom_workshop_task ?? '',
  set: (value: string) => {
    model.value = { ...model.value, custom_workshop_task: value };
  },
});

const locationStatus = ref('');

function resetSerialLookup(): void {
  locationStatus.value = '';
}

let locationController: AbortController | undefined;

async function handleSerialBlur(): Promise<void> {
  resetSerialLookup();
  const { model_id: modelId, serial } = model.value;
  if (!modelId || !serial) return;
  locationController?.abort();
  locationController = new AbortController();
  const { signal } = locationController;
  try {
    const location = await fetchGetProductLocation(modelId, serial, { signal });
    if (signal.aborted) return;
    const parts: string[] = [];
    if (location.warehouse) parts.push(`Warehouse: ${location.warehouse}`);
    if (location.name) parts.push(`Status: ${location.name}`);
    if (parts.length > 0) {
      locationStatus.value = parts.join(', ');
      model.value = { ...model.value, quantity: 1 };
    } else {
      locationStatus.value = 'Out of stock';
    }
  } catch {
    if (signal.aborted) return;
    locationStatus.value = 'Out of stock';
  }
}

function handleModelUpdate(value: string): void {
  resetSerialLookup();
  model.value = { ...model.value, model: value, model_id: null };
}

function handleModelSelectItem(
  item: ProductModelLookupItem | FinisherModelLookupItem,
): void {
  model.value = {
    ...model.value,
    model: item.name,
    model_id: item.model_id,
  };
}

function handleQuantityUpdate(value: number): void {
  model.value = { ...model.value, quantity: value };
}

function handleServiceUpdate(value: InstallationValue | ReturnValue): void {
  if (props.isReturnLeg) {
    model.value = { ...model.value, returns: value as ReturnValue };
  } else {
    model.value = { ...model.value, installation: value as InstallationValue };
  }
}

const serviceValue = computed(() =>
  props.isReturnLeg ? model.value.returns : model.value.installation,
);
</script>

<template>
  <div :class="$style.jobBookingProductRow" data-testid="jobBookingProductRow">
    <div :class="$style.mainRow">
      <div :class="$style.cell">
        <FinisherModelLookup
          v-if="model.finisher"
          :model-value="model.model"
          :customer-code="props.customerCode"
          size="md"
          :rules="[requiredValidationRule]"
          validate-on="input"
          @update:model-value="handleModelUpdate"
          @select-item="handleModelSelectItem"
        />
        <ProductModelLookup
          v-else
          :model-value="model.model"
          size="md"
          :rules="[requiredValidationRule]"
          validate-on="input"
          @update:model-value="handleModelUpdate"
          @select-item="handleModelSelectItem"
        />
        <span v-if="props.modelWarning" :class="$style.warning">
          {{ props.modelWarning }}
        </span>
      </div>
      <KTextField
        v-model="model.serial"
        label="Serial #"
        size="md"
        :messages="locationStatus"
        @blur="handleSerialBlur"
      />
      <KTextField v-model="model.description" label="Description" size="md" />
      <KNumberStepperField
        :model-value="model.quantity"
        :min="1"
        :max="999"
        size="md"
        @update:model-value="handleQuantityUpdate"
      />
      <div
        v-if="!props.isReturnLeg && model.installation === 'FNSHR'"
        :class="$style.finisherBadgeCell"
      >
        <span :class="$style.finisherBadge">FINISHER</span>
      </div>
      <JobBookingServiceOrReturnField
        v-else
        :model-value="serviceValue"
        :is-return-leg="props.isReturnLeg"
        size="md"
        @update:model-value="handleServiceUpdate"
      />
      <div :class="$style.workshopToggleCell">
        <span :class="$style.workshopToggleLabel">Required</span>
        <KSwitchField v-model="workshopRequired" is-small inset />
        <v-btn
          v-if="props.isDeletable"
          icon="mdi-trash-can-outline"
          size="x-small"
          variant="text"
          color="error"
          aria-label="Delete product"
          :class="$style.deleteBtn"
          @click="handleDeleteClick"
        />
      </div>
    </div>

    <div v-if="workshopRequired" :class="$style.workshopRow">
      <KMultipleSelectField
        v-model="predefinedTasksValue"
        label="Predefined workshop tasks"
        size="md"
        :items="PREDEFINED_WORKSHOP_TASK_OPTIONS"
        :plus-count="PREDEFINED_WORKSHOP_TASK_OPTIONS.length"
      />
      <KTextField
        v-model="customTaskValue"
        label="Custom workshop tasks"
        size="md"
      />
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobBookingProductRow {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid colors.$color-gray-100;

  &:last-child {
    border-bottom: none;
  }
}
  .mainRow {
    display: grid;
    grid-template-columns: minmax(160px, 1.3fr) minmax(140px, 1fr) minmax(180px, 1.5fr) auto minmax(160px, 1.2fr) auto;
    gap: 16px;
    align-items: start;
  }
  .cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .warning {
    font-size: 0.75rem;
    color: colors.$color-yellow-500;
  }
  .workshopRow {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .finisherBadgeCell {
    display: flex;
    align-items: center;
    height: variables.$btn-md-height;
  }
  .finisherBadge {
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: colors.$color-text-primary;
  }
  .workshopToggleCell {
    display: flex;
    align-items: center;
    gap: 8px;
    height: variables.$btn-md-height;
    white-space: nowrap;

    :global {
      .v-input__details {
        display: none;
      }
    }
  }
  .workshopToggleLabel {
    font-size: 0.8125rem;
    color: colors.$color-text-primary;
    white-space: nowrap;
  }
  .deleteBtn {
    margin-left: 4px;
  }
</style>
